import type { ColDef } from "ag-grid-community";

import { Constants, compareTimeSlots } from "@eagerdog/constants";
import { IEventSummary, IShow, IRunOff } from "@eagerdog/interfaces";

import type {
  IResultCommonCols,
  IAKCObedienceCols,
  IAKCRallyObedienceCols,
  IAKCScentworkCols,
  IUKCNoseworkCols,
  IAKCFastCATCols,
  IAKCFetchCols,
  IABIRatRaceCols,
  IUKCDockJumpingCols,
  IUKCWeightPullCols,
  IPWDCAWaterDogCols,
  IUKCPrecisionCoursingCols,
  IUKCShedDogCols,
  IConformationCols,
  IAKCFarmDogCols,
  IAKCHerdingCols,
  IUkcAgilityCols,
  IAkcAgilityCols,
  ICWAGSScentCols,
  IABIScentDogGamesCols,
} from "./ColumnTypes";

import { IOption } from "src/components/Dropdown/Dropdown";

import { ResultRenderer } from "src/routes/EventManage/ResultsTab/ResultRenderer";
import { StatusRenderer } from "src/routes/EventManage/ResultsTab/StatusRenderer";
import { ActionsRenderer } from "src/routes/EventManage/ResultsTab/ActionsRenderer";
import { EventTitleRenderer } from "./EventTitleRenderer";
import { CheckInRenderer } from "../../routes/EventManage/ResultsTab/CheckInRenderer";
import { CompleteRenderer } from "../../routes/EventManage/ResultsTab/CompleteRenderer";
import { apiService } from "src/services/api.service";

// Numeric comparator for score/time columns — handles mixed string/number values
// and sends empty/non-numeric values to the bottom on both asc and desc sorts.
const numericComparator = (valueA: any, valueB: any) => {
  const a = parseFloat(valueA);
  const b = parseFloat(valueB);
  if (isNaN(a) && isNaN(b)) return 0;
  if (isNaN(a)) return -1;
  if (isNaN(b)) return 1;
  return a - b;
};

// Handle checking/unchecking dogs in the Results tab
const handleCheckInChange = async (scoreId: string, checked: boolean, eventId: string, fetchData: (id: string) => void) => {
  try {
    await apiService.updateScoreCheckIn(eventId, scoreId, checked);
    // Refresh grid row data so the saved value is reflected in scores state.
    // This also covers cases where the WebSocket broadcast does not fire
    // (e.g. show has no run-order rings configured).
    fetchData(eventId);
  } catch (error) {
    console.error('Error updating check-in status:', error);
    // Refresh to revert on error
    fetchData(eventId);
    throw error;
  }
};

// Handle checking/unchecking complete status
const handleCompleteChange = async (scoreId: string, complete: boolean, eventId: string, fetchData: (id: string) => void) => {
  try {
    await apiService.updateScoreComplete(eventId, scoreId, complete);
    // Refresh grid row data so the saved value is reflected in scores state.
    fetchData(eventId);
  } catch (error) {
    console.error('Error updating complete status:', error);
    // Refresh to revert on error
    fetchData(eventId);
    throw error;
  }
};

export interface IScoresGridActionOptions {
  // Called with fresh shows after a results save so the grid's action menu
  // (and the Enter Results form it opens) sees updated judge-schedule tiles.
  onShowsChange?: (shows: IShow[]) => void;
  // All scores currently loaded for the event; lets Enter Results fall back to
  // values from an already-entered dog in the same class.
  scores?: any[];
}

const getCommonCols = (selectedShow: IOption, event?: IEventSummary, shows?: IShow[], fetchData?: (id: string) => void, isUserView?: boolean, actionOptions?: IScoresGridActionOptions): ColDef<IResultCommonCols>[] => {
  let cols: ColDef<IResultCommonCols>[] = [
    {
      headerName: "Checked-In",
      field: "checkedIn",
      width: 130,
      minWidth: 130,
      maxWidth: 130,
      suppressSizeToFit: true,
      cellRenderer: CheckInRenderer,
      cellRendererParams: {
        onCheckInChange: (scoreId: string, checked: boolean) => {
          if (fetchData && event) {
            return handleCheckInChange(scoreId, checked, event._id, fetchData);
          }
          return Promise.resolve();
        }
      },
      cellStyle: { 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center'
      } as any,
      headerTooltip: "Checked-In",
      hide: isUserView // Hide for user view, show for club/admin
    },
    {
      headerName: "Complete",
      field: "complete",
      width: 130,
      minWidth: 130,
      maxWidth: 130,
      suppressSizeToFit: true,
      cellRenderer: CompleteRenderer,
      cellRendererParams: {
        onCompleteChange: (scoreId: string, complete: boolean) => {
          if (fetchData && event) {
            return handleCompleteChange(scoreId, complete, event._id, fetchData);
          }
          return Promise.resolve();
        }
      },
      cellStyle: { 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center'
      } as any,
      headerTooltip: "Complete",
      hide: isUserView // Hide for user view, show for club/admin
    },
    { field: "show_id", filter: true, hide: true },
    { field: "dog_id", minWidth: 100, filter: true, hide: true },
    { 
      field: "eventTitle", 
      headerName: "Event Title", 
      minWidth: 180, 
      filter: true, 
      hide: !!event,
      cellRenderer: isUserView ? EventTitleRenderer : undefined
    },
    { field: "eventDate", minWidth: 180, filter: true, hide: !!event || isUserView }, // Hide for user view
    { field: "show", width: 180, minWidth: 160, maxWidth: 180,suppressSizeToFit: true, filter: true, hide: isUserView || selectedShow.value !== 'None' }, // Hide for user view
    { 
      field: "showType", 
      headerName: "Show Type", 
      minWidth: 150, 
      filter: true, 
      hide: !isUserView,
      cellRenderer: (params: any) => {
        const showType = params.value;
        // Return the formatted show type from Constants
        return showType && Constants.show_type[showType as keyof typeof Constants.show_type] 
          ? Constants.show_type[showType as keyof typeof Constants.show_type]
          : showType || "";
      }
    }, // Show only for user view
    { field: "showName", headerName: "Show Name", minWidth: 160, filter: true, hide: !isUserView }, // Show only for user view
    { 
      field: "showDate", 
      headerName: "Show Date", 
      minWidth: 170, 
      filter: true, 
      hide: !isUserView && selectedShow.value !== 'None',
      comparator: (valueA: string, valueB: string) => {
        // Parse dates in MM/DD/YYYY format for proper sorting
        const dateA = new Date(valueA);
        const dateB = new Date(valueB);
        return dateA.getTime() - dateB.getTime();
      }
    },
    { field: "armband", width: 130, minWidth: 130, maxWidth: 180, suppressSizeToFit: true, filter: true, comparator: numericComparator, hide: isUserView || selectedShow.data?.show_type === Constants.show_type.fast_cat || selectedShow.data?.show_type === Constants.show_type.farm_dog },
    {
      field: "result",
      minWidth: 120,
      maxWidth: 120,
      filter: true,
      cellRenderer: ResultRenderer,
    },
    {
      field: "status",
      minWidth: 120,
      maxWidth: 120,
      filter: true,
      cellRenderer: StatusRenderer,
    },
    { field: "registeredName", width: 150, minWidth: 200, maxWidth: 300,suppressSizeToFit: true, filter: true, hide: isUserView },
    { field: "callName", width: 180, minWidth: 160, maxWidth: 180, suppressSizeToFit: true, filter: true, comparator: (a: string, b: string) => {
      const nameA = (a ?? '').replace(/\s*\([DB]\)$/i, '').trim();
      const nameB = (b ?? '').replace(/\s*\([DB]\)$/i, '').trim();
      return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
    }},
    {
      field: "judge",
      headerName: "Judge",
      minWidth: 160,
      filter: true,
      hide: selectedShow.data?.show_type !== Constants.show_type.farm_dog,
    },
    {
      field: "trialJumpHeight",
      headerName: "Jump Height",
      width: 145,
      minWidth: 145,
      maxWidth: 170,
      suppressSizeToFit: true,
      filter: true,
      hide: !(event?.sanctioning_club === Constants.sanctioning_club.AKC &&
              !!selectedShow.data &&
              [Constants.show_type.obedience, Constants.show_type.rally_obedience].includes(selectedShow.data.show_type)),
    },
    { 
      field: "breedGroup", 
      headerName: "Breed Group",
      minWidth: 150, 
      filter: true, 
      hide: selectedShow.data?.show_type !== Constants.show_type.conformation 
    },
    { 
      field: "breed", 
      minWidth: 120, 
      filter: true, 
      hide: selectedShow.data?.show_type !== Constants.show_type.conformation && selectedShow.data?.show_type !== Constants.show_type.fast_cat
    },
    { 
      field: "ownerName", 
      minWidth: 150, 
      filter: true, 
      hide: true
    },
    {
      field: "element",
      minWidth: 150,
      filter: true,
      hide: selectedShow.data?.show_type === Constants.show_type.fast_cat || 
            selectedShow.data?.show_type === Constants.show_type.cat,
    },
    { 
      field: "level", 
      minWidth: 150, 
      filter: true,
      hide: selectedShow.data?.show_type === Constants.show_type.fast_cat || 
            selectedShow.data?.show_type === Constants.show_type.cat ||
            selectedShow.data?.show_type === Constants.show_type.farm_dog,
      cellRenderer: (params: any) => {
        const level = params.data.level || "";
        const groupClass = params.data.groupClass || "";
        return groupClass ? `${level} (${groupClass})` : level;
      }
    },
    {
      field: "jump_height",
      headerName: "Jump Height",
      minWidth: 165,
      maxWidth: 165,
      width: 165,
      suppressSizeToFit: true,
      filter: true,
      hide: selectedShow.data?.show_type !== Constants.show_type.agility || event?.sanctioning_club !== Constants.sanctioning_club.AKC,
    },
    {
      field: "notes",
      headerName: "Notes",
      minWidth: 220,
      filter: true,
      hide: selectedShow.data?.show_type !== Constants.show_type.cat,
      valueFormatter: (params: any) => params.value || "",
    },
    {
      field: "conflictNote",
      headerName: "Conflict Note",
      minWidth: 220,
      filter: true,
      hide: isUserView,
      valueFormatter: (params: any) => params.value || "",
    },
  ];

  // Reorder columns for user view
  if (isUserView) {
    const fieldOrder = ['showType', 'showName', 'showDate', 'element', 'level', 'callName', 'status', 'result', 'notes'];
    cols = fieldOrder
      .map(fieldName => cols.find(col => col.field === fieldName))
      .filter(col => col !== undefined) as ColDef<IResultCommonCols>[];
  }

  // Only add actions column for admin/club manager view, not for user view
  if (event && shows && fetchData && !isUserView) {
    cols = [
      ...cols,
      {
        headerName: "",
        field: "actions",
        maxWidth: 50,
        minWidth: 50,
        cellRenderer: ActionsRenderer,
        pinned: "right",
        cellRendererParams: {
          getResults: () => { fetchData(event._id) },
          event: event,
          shows: shows,
          scores: actionOptions?.scores,
          onShowsChange: actionOptions?.onShowsChange,
        },
        resizable: false,
        cellStyle: {
          "overflow": "visible",
          "zIndex": "2"
        }
      }
    ]
  }

  return cols;
}

type IResultColumnsType =
  | IAKCObedienceCols
  | IAKCRallyObedienceCols
  | IAKCScentworkCols
  | IUKCNoseworkCols
  | IAKCFastCATCols
  | IAKCFetchCols
  | IABIRatRaceCols
  | IUKCDockJumpingCols
  | IUKCWeightPullCols
  | IPWDCAWaterDogCols
  | IUKCPrecisionCoursingCols
  | IUKCShedDogCols
  | IConformationCols
  | IAKCFarmDogCols
  | IAKCHerdingCols
  | IAkcAgilityCols
  | ICWAGSScentCols
  | IABIScentDogGamesCols

type ToArray<T> = T extends any ? T[] : never;
type IResultRows = ToArray<IResultColumnsType>;

const getColumns = (selectedShow: IOption, event?: IEventSummary, shows?: IShow[], fetchData?: (id: string) => void, runOffs?: IRunOff[], isUserView?: boolean, actionOptions?: IScoresGridActionOptions): ColDef<IResultColumnsType>[] => {
  const defCols = getCommonCols(selectedShow, event, shows, fetchData, isUserView, actionOptions);

  switch (selectedShow.data?.show_type) {
    case Constants.show_type.obedience:
      // For AKC Obedience, add placement column
      const obedienceCols: ColDef<IAKCObedienceCols>[] = [...defCols];
      
      // For C-WAGS Obedience, add section and score columns
      if (event?.sanctioning_club === Constants.sanctioning_club.C_WAGS) {
        const levelIndex = obedienceCols.findIndex(col => col.field === "level");
        obedienceCols.splice(levelIndex + 1, 0, { field: "section", minWidth: 120, filter: true });
        const cwagResultIdx = obedienceCols.findIndex(col => col.field === "result");
        obedienceCols.splice(cwagResultIdx + 1, 0, { 
          field: "score", 
          headerName: "Score", 
          minWidth: 120, 
          filter: true,
          comparator: numericComparator,
          valueFormatter: (params: any) => {
            // Return empty string if no score, otherwise show the score
            return params.value === "" || params.value === null || params.value === undefined ? "" : params.value;
          }
        });
        return obedienceCols;
      }
      
      // Add placement column after "result" and before "totalDeductions"
      if (event?.sanctioning_club === Constants.sanctioning_club.AKC || event?.sanctioning_club === Constants.sanctioning_club.UKC) {
        obedienceCols.push({
          field: "placement",
          headerName: "Placement",
          minWidth: 120,
          maxWidth: 120,
          filter: true,
          cellRenderer: (params: any) => {
            if (!params.data.placement) return "";
            
            const scoreId = params.data.scoreId != null ? String(params.data.scoreId) : "";
            const isRunOffWinner = runOffs?.some(ro => {
              if (!ro.resolved_at || ro.level !== params.data.level) return false;
              const winnerByScore = !!scoreId && !!ro.ordered_placements?.some(
                (p) => p.placement === 1 && String(p.score_id) === scoreId
              );
              const winnerByArm =
                !!ro.winner_arm_number &&
                !!params.data.armband &&
                ro.winner_arm_number === params.data.armband;
              return winnerByScore || winnerByArm;
            });
            
            if (isRunOffWinner) {
              return `${params.data.placement} 🏆`;
            }
            
            return params.data.placement;
          },
        });
      }
      
      obedienceCols.push(
        { field: "totalDeductions", minWidth: 150, filter: true },
      );
      const obedienceResultIdx = obedienceCols.findIndex(col => col.field === "result");
      obedienceCols.splice(obedienceResultIdx + 1, 0, { 
        field: "score", 
        minWidth: 120, 
        filter: true,
        comparator: numericComparator,
        cellRenderer: (params: any) => {
          const score = params.value;
          return score !== undefined && score !== null && score !== "" ? score : "";
        }
      });
      
      return obedienceCols;
    case Constants.show_type.rally_obedience:
      // For C-WAGS Rally, add section, time, and score columns
      if (event?.sanctioning_club === Constants.sanctioning_club.C_WAGS) {
        const rallyCols: ColDef<IAKCRallyObedienceCols>[] = [...defCols];
        const levelIndex = rallyCols.findIndex(col => col.field === "level");
        rallyCols.splice(levelIndex + 1, 0, { field: "section", minWidth: 120, filter: true });
        const cwagRallyResultIdx = rallyCols.findIndex(col => col.field === "result");
        rallyCols.splice(cwagRallyResultIdx + 1, 0,
          { 
            field: "score", 
            headerName: "Score", 
            minWidth: 120, 
            filter: true,
            comparator: numericComparator,
            valueFormatter: (params: any) => {
              return params.value === "" || params.value === null || params.value === undefined ? "" : params.value;
            }
          },
          { field: "time", headerName: "Time", minWidth: 120, filter: true }
        );
        return rallyCols;
      }

      const rallyCols: ColDef<IAKCRallyObedienceCols>[] = [...defCols];
      // UKC Rally: Level is RO1/RO2/RO3/ROM and the A/B/C class sits in its own Section column
      if (event?.sanctioning_club === Constants.sanctioning_club.UKC) {
        const ukcLevelIndex = rallyCols.findIndex(col => col.field === "level");
        rallyCols.splice(ukcLevelIndex + 1, 0, { field: "section", headerName: "Section", minWidth: 120, maxWidth: 130, filter: true });
      }
      rallyCols.push(
        { field: "totalDeductions", minWidth: 150, filter: true },
      );
      const rallyResultIdx = rallyCols.findIndex(col => col.field === "result");
      rallyCols.splice(rallyResultIdx + 1, 0,
        { 
          field: "score", 
          minWidth: 120, 
          filter: true,
          comparator: numericComparator,
          cellRenderer: (params: any) => {
            const score = params.value;
            return score !== undefined && score !== null && score !== "" ? score : "";
          }
        },
        { field: "time", minWidth: 120, filter: true }
      );
      return rallyCols;
    case Constants.show_type.scentwork:
      return [...defCols, ...[
        { field: "resultReason", minWidth: 175, filter: true },
        { field: "searchTime", minWidth: 170, filter: true },
        { field: "faults", minWidth: 130, filter: true },
        { field: "numFinds", minWidth: 150, filter: true },
        { field: "placement", minWidth: 150, filter: true },
      ]] as ColDef<IAKCScentworkCols>[];
    case Constants.show_type.nosework: {
      // Hide Registered Name for UKC Nosework — Call Name is used instead.
      const noseworkColumns = defCols.map(col =>
        col.field === "registeredName" ? { ...col, hide: true } : col
      ) as ColDef<IUKCNoseworkCols>[];

      const allNoseworkCols: ColDef<IUKCNoseworkCols>[] = [
        ...noseworkColumns,
        { field: "section", minWidth: 120, filter: true },
        { field: "resultReason", minWidth: 175, filter: true },
        { field: "searchTime", minWidth: 170, filter: true },
        { field: "excludedFromPlacement", minWidth: 250, filter: true },
      ];

      // Fixed display order for this show type; any columns not listed
      // (e.g. hidden ones like show_id, or the pinned actions column)
      // are left in their existing position — pinned columns are
      // unaffected by array order.
      const noseworkFieldOrder = [
        "checkedIn",
        "complete",
        "armband",
        "result",
        "searchTime",
        "callName",
        "element",
        "level",
        "section",
        "status",
        "conflictNote",
        "resultReason",
        "excludedFromPlacement",
      ];

      const orderedNoseworkCols = noseworkFieldOrder
        .map(field => allNoseworkCols.find(col => col.field === field))
        .filter((col): col is ColDef<IUKCNoseworkCols> => col !== undefined);

      const remainingNoseworkCols = allNoseworkCols.filter(
        col => !noseworkFieldOrder.includes(col.field as string)
      );

      return [...remainingNoseworkCols, ...orderedNoseworkCols] as ColDef<IUKCNoseworkCols>[];
    }
    case Constants.show_type.fast_cat:
      const fastCatCols = [...defCols] as ColDef<IAKCFastCATCols>[];
      const fastCatResultIdx = fastCatCols.findIndex(col => col.field === "result");
      fastCatCols.splice(fastCatResultIdx + 1, 0,
        { field: "timeSlot", minWidth: 120, filter: true },
        { field: "time", minWidth: 120, filter: true },
      );
      return fastCatCols;
    case Constants.show_type.fetch:
      return [...defCols] as ColDef<IAKCFetchCols>[];
    case Constants.show_type.urban_rat_race:
      const urbanRatRaceColumns = defCols as ColDef<IABIRatRaceCols>[];
      const urbanRatRaceLevelIndex = defCols.findIndex(col => col.field === "level");
      urbanRatRaceColumns.splice(urbanRatRaceLevelIndex + 1, 0, { field: "section", minWidth: 120, filter: true })
      const urbanRatRaceResultIdx = urbanRatRaceColumns.findIndex(col => col.field === "result");
      urbanRatRaceColumns.splice(urbanRatRaceResultIdx + 1, 0,
        { field: "time", headerName: "Time", minWidth: 120, filter: true },
      );
      return [...urbanRatRaceColumns, ...[
        { field: "faultCount", headerName: "Fault Count", minWidth: 150, filter: true },
        { field: "ratsFound", headerName: "Rats Found", minWidth: 150, filter: true },
        { field: "finalTime", headerName: "Final Time", minWidth: 150, filter: true },
      ]]  as ColDef<IABIRatRaceCols>[];
    case Constants.show_type.country_rat_race:
      const countryRatRaceColumns = defCols as ColDef<IABIRatRaceCols>[];
      const countryRatRaceLevelIndex = defCols.findIndex(col => col.field === "level");
      countryRatRaceColumns.splice(countryRatRaceLevelIndex + 1, 0, { field: "section", minWidth: 120, filter: true })
      const countryRatRaceResultIdx = countryRatRaceColumns.findIndex(col => col.field === "result");
      countryRatRaceColumns.splice(countryRatRaceResultIdx + 1, 0,
        { field: "time", headerName: "Time", minWidth: 120, filter: true },
      );
      return [...countryRatRaceColumns, ...[
        { field: "faultCount", headerName: "Fault Count", minWidth: 150, filter: true },
        { field: "ratsFound", headerName: "Rats Found", minWidth: 150, filter: true },
        { field: "finalTime", headerName: "Final Time", minWidth: 150, filter: true },
      ]]  as ColDef<IABIRatRaceCols>[];
    case Constants.show_type.lost_and_found:
      const lfColumns = defCols as ColDef<IABIRatRaceCols>[];
      const lfLevelIndex = defCols.findIndex(col => col.field === "level");
      lfColumns.splice(lfLevelIndex + 1, 0, { field: "section", minWidth: 120, filter: true });
      const lfResultIdx = lfColumns.findIndex(col => col.field === "result");
      lfColumns.splice(lfResultIdx + 1, 0,
        { field: "time", headerName: "Time", minWidth: 120, filter: true },
      );
      return [...lfColumns, ...[
        { field: "faultCount", headerName: "Fault Count", minWidth: 150, filter: true },
        { field: "finalTime", headerName: "Final Time", minWidth: 150, filter: true },
      ]] as ColDef<IABIRatRaceCols>[];
    case Constants.show_type.dock_jumping:
      return [...defCols, ...[
        { field: "feet", minWidth: 120, filter: true },
        { field: "inches", minWidth: 120, filter: true },
      ]] as ColDef<IUKCDockJumpingCols>[];
    case Constants.show_type.weight_pull:
      const weightPullCols = [...defCols] as ColDef<IUKCWeightPullCols>[];
      const weightPullResultIdx = weightPullCols.findIndex(col => col.field === "result");
      weightPullCols.splice(weightPullResultIdx + 1, 0,
        { field: "time", minWidth: 150, filter: true },
      );
      return [...weightPullCols, ...[
        { field: "weightClass", minWidth: 150, filter: true },
        { field: "dogWeight", minWidth: 150, filter: true },
        { field: "highestWeight", minWidth: 150, filter: true },
        { field: "percentPulled", minWidth: 150, filter: true },
      ]] as ColDef<IUKCWeightPullCols>[];
    case Constants.show_type.water_dog:
      const waterDogCols = [...defCols] as ColDef<IPWDCAWaterDogCols>[];
      const waterDogResultIdx = waterDogCols.findIndex(col => col.field === "result");
      waterDogCols.splice(waterDogResultIdx + 1, 0,
        { field: "time", minWidth: 120, filter: true },
      );
      return waterDogCols;
    case Constants.show_type.precision_coursing:
      return [...defCols, ...[
        { field: "searchTime", minWidth: 120, filter: true },
      ]] as ColDef<IUKCPrecisionCoursingCols>[];
    case Constants.show_type.shed_dog:
      return [...defCols, ...[
        { field: "searchTime", minWidth: 120, filter: true },
      ]] as ColDef<IUKCShedDogCols>[];
    case Constants.show_type.conformation:
      return [...defCols] as ColDef<IConformationCols>[];
    case Constants.show_type.herding:
    case Constants.show_type.pre_trial_herding:
    case Constants.show_type.instinct_test_herding:
      const herdingColumns = defCols as ColDef<IAKCHerdingCols>[];
      const herdingLevelIndex = defCols.findIndex(col => col.field === "level");
      herdingColumns.splice(herdingLevelIndex + 1, 0, { field: "section", minWidth: 120, filter: true })
      herdingColumns.push(
        { field: "elapsedTime", minWidth: 120, filter: true },
        { field: "placement", minWidth: 120, filter: true },
      );
      const herdingResultIdx = herdingColumns.findIndex(col => col.field === "result");
      herdingColumns.splice(herdingResultIdx + 1, 0, { field: "score", minWidth: 120, filter: true, comparator: numericComparator });
      return herdingColumns;
    case Constants.show_type.agility:
      if (event?.sanctioning_club === Constants.sanctioning_club.AKC) {
        const akcAgilityCols: ColDef<IAkcAgilityCols>[] = [...defCols];
        (akcAgilityCols as any[]).push(
          { field: "timeToCompete", minWidth: 190, filter: true },
          { field: "courseTime", minWidth: 160, filter: true },
          { field: "totalFaults", minWidth: 160, filter: true },
          { field: "placement", minWidth: 150, filter: true },
        );
        const akcAgilityResultIdx = akcAgilityCols.findIndex(col => col.field === "result");
        (akcAgilityCols as any[]).splice(akcAgilityResultIdx + 1, 0, { field: "score", minWidth: 120, filter: true, comparator: numericComparator });
        return akcAgilityCols;
      }

      const ukcAgilityCols: ColDef<IUkcAgilityCols>[] = [...defCols];
      const resultIndex = ukcAgilityCols.findIndex((col) => col.field === "result");
      const insertAt = resultIndex >= 0 ? resultIndex + 1 : ukcAgilityCols.length;

      ukcAgilityCols.splice(
        insertAt,
        0,
        {
          field: "score",
          headerName: "Score",
          minWidth: 120,
          filter: true,
          comparator: numericComparator,
          cellRenderer: (params: any) => {
            const score = params.value;
            return score !== undefined && score !== null && score !== "" ? score : "";
          }
        },
        { field: "time", headerName: "Time", minWidth: 130, filter: true },
        {
          field: "faults",
          headerName: "Faults",
          minWidth: 130,
          filter: true,
          cellRenderer: (params: any) => {
            const faults = params.value;
            return faults !== undefined && faults !== null && faults !== "" ? faults : "";
          }
        }
      );

      if (event?.sanctioning_club === Constants.sanctioning_club.UKC) {
        ukcAgilityCols.push({
          field: "timeFaults",
          headerName: "Time Faults",
          minWidth: 130,
          filter: true,
          cellRenderer: (params: any) => {
            const timeFaults = params.value;
            return timeFaults !== undefined && timeFaults !== null && timeFaults !== "" ? timeFaults : "";
          }
        });
      }

      ukcAgilityCols.push({ field: "excludedFromPlacement", minWidth: 150, filter: true });

      // For 4-H Agility, show the Section column (Division A/B) next to Level
      if (event?.sanctioning_club === Constants.sanctioning_club.FH) {
        const fhAgilityLevelIdx = ukcAgilityCols.findIndex(col => col.field === "level");
        ukcAgilityCols.splice(fhAgilityLevelIdx + 1, 0, { field: "section", headerName: "Section", minWidth: 130, filter: true });
      }

      return ukcAgilityCols;
    case Constants.show_type.scent: {
      const scentCols: ColDef<ICWAGSScentCols>[] = [...defCols];
      const scentLevelIdx = scentCols.findIndex(col => col.field === "level");
      if (scentLevelIdx !== -1) {
        scentCols.splice(scentLevelIdx + 1, 0, {
          field: "section",
          headerName: "FEO",
          minWidth: 80,
          maxWidth: 90,
          filter: true,
          valueFormatter: (params: any) =>
            params.value === Constants.dog_class_section.feo ? "FEO" : "",
        });
      }
      return [...scentCols, ...[
        { field: "scents", minWidth: 120, filter: true },
        { field: "fault1", minWidth: 120, filter: true },
        { field: "fault2", minWidth: 120, filter: true },
        { field: "searchTime", minWidth: 120, filter: true },
      ]] as ColDef<ICWAGSScentCols>[];
    }
    case Constants.show_type.cwags_games: {
      const gamesCols = [...defCols] as ColDef<any>[];
      const gamesLevelIdx = gamesCols.findIndex(col => col.field === "level");
      if (gamesLevelIdx !== -1) {
        gamesCols.splice(gamesLevelIdx + 1, 0, {
          field: "section",
          headerName: "FEO",
          minWidth: 80,
          maxWidth: 90,
          filter: true,
          valueFormatter: (params: any) =>
            params.value === Constants.dog_class_section.feo ? "FEO" : "",
        });
      }
      return gamesCols;
    }

    case Constants.show_type.scent_dog_games: {
      let sdgCols = [...defCols] as ColDef<IABIScentDogGamesCols>[];

      // Hide status column
      sdgCols = sdgCols.map(col =>
        col.field === "status" ? { ...col, hide: true } : col
      );

      // Pull element, level, registeredName, callName out — placed manually at the end
      const elementCol = sdgCols.find(col => col.field === "element");
      const levelCol = sdgCols.find(col => col.field === "level");
      const registeredNameCol = sdgCols.find(col => col.field === "registeredName");
      const callNameCol = sdgCols.find(col => col.field === "callName");
      sdgCols = sdgCols.filter(col =>
        col.field !== "element" &&
        col.field !== "level" &&
        col.field !== "registeredName" &&
        col.field !== "callName"
      );

      return [
        ...sdgCols,
        { field: "faultCount", headerName: "Faults", minWidth: 120, filter: true, valueFormatter: (params: any) => (params.value === "" || params.value === null || params.value === undefined) ? "" : params.value },
        { field: "searchTime", headerName: "Time", minWidth: 120, filter: true },
        { field: "effectiveTime", headerName: "Effective Time", minWidth: 170, filter: true },
        { field: "placement", minWidth: 145, filter: true },
        ...(elementCol ? [elementCol] : []),
        ...(levelCol ? [levelCol] : []),
        { field: "section", headerName: "Section", minWidth: 120, filter: true },
        ...(registeredNameCol ? [registeredNameCol] : []),
        ...(callNameCol ? [callNameCol] : []),
      ] as ColDef<IABIScentDogGamesCols>[];
    }

    case Constants.show_type.barn_hunt: {
      const barnHuntCols = [...defCols] as ColDef<any>[];
      // Move the existing callName column to sit right after armband
      const callNameIdx = barnHuntCols.findIndex(col => col.field === "callName");
      const armbandIdx = barnHuntCols.findIndex(col => col.field === "armband");
      if (callNameIdx !== -1 && armbandIdx !== -1 && callNameIdx !== armbandIdx + 1) {
        const [callNameCol] = barnHuntCols.splice(callNameIdx, 1);
        const insertAt = barnHuntCols.findIndex(col => col.field === "armband") + 1;
        barnHuntCols.splice(insertAt, 0, callNameCol);
      }
      // Insert Section and Height columns right after Level
      const barnHuntLevelIdx = barnHuntCols.findIndex(col => col.field === "level");
      barnHuntCols.splice(barnHuntLevelIdx + 1, 0,
        { field: "section", headerName: "Section", minWidth: 130, maxWidth: 130, filter: true },
        { field: "heightDivision", headerName: "Division", minWidth: 130, maxWidth: 130, filter: true },
      );
      const barnHuntResultIdx = barnHuntCols.findIndex(col => col.field === "result");
      barnHuntCols.splice(barnHuntResultIdx + 1, 0,
        { field: "time", headerName: "Time", minWidth: 120, filter: true },
        {
          field: "score",
          headerName: "Score",
          minWidth: 110,
          maxWidth: 110,
          filter: true,
          comparator: numericComparator,
          valueFormatter: (params: any) =>
            params.value === "" || params.value === null || params.value === undefined
              ? ""
              : params.value,
        },
        { field: "placement", headerName: "Placement", minWidth: 150, filter: true },
      );
      return barnHuntCols;
    }

    // General Obedience / Rally Show N Go: each dog picks a time slot per class,
    // so show it alongside the class and order columns like a schedule.
    case Constants.show_type.obedience_show_n_go:
    case Constants.show_type.general_rally: {
      const timeSlotCol: ColDef<any> = {
        field: "timeSlot",
        headerName: "Time Slot",
        minWidth: 180,
        filter: true,
        comparator: (a: string, b: string) => compareTimeSlots(a, b),
        valueFormatter: (params: any) => params.value || "",
      };

      const showNGoCols = [...defCols] as ColDef<any>[];
      const showNGoResultIdx = showNGoCols.findIndex(col => col.field === "result");
      showNGoCols.splice(showNGoResultIdx + 1, 0, timeSlotCol);

      // The user view has its own ordering; only re-order the admin/club view.
      if (isUserView) {
        return showNGoCols;
      }

      const showNGoFieldOrder = [
        "checkedIn",
        "complete",
        "armband",
        "result",
        "timeSlot",
        "element",
        "level",
        "callName",
        "registeredName",
        "status",
        "conflictNote",
      ];

      const orderedShowNGoCols = showNGoFieldOrder
        .map(field => showNGoCols.find(col => col.field === field))
        .filter((col): col is ColDef<any> => col !== undefined);

      // Anything not listed (hidden ids, show/date columns, the pinned actions column)
      // keeps its relative position ahead of the ordered set.
      const remainingShowNGoCols = showNGoCols.filter(
        col => !showNGoFieldOrder.includes(col.field as string)
      );

      return [...remainingShowNGoCols, ...orderedShowNGoCols];
    }

    default:
      return defCols;
  }
}

export { type IResultColumnsType, type IResultRows, getColumns }
